import styles from "./task-page.module.css";
import { useState, useEffect, useContext, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import routes from "@/routes";
import dayjs from "dayjs";

import {
  Map,
  FullscreenControl,
  Marker,
  NavigationControl,
} from "@vis.gl/react-maplibre";

import {
  Flex,
  Typography,
  Spin,
  Tag,
  Card,
  App,
  Button,
  Modal,
  Tooltip,
  Upload,
  Tabs,
  Dropdown,
  ConfigProvider,
  notification,
  Divider,
  Badge,
} from "antd";

import { UserContext } from "@/context/UserContext";
import MainContext from "@/layouts/MainLayout/context";

import {
  formatISODateToCustomStr,
  copyLinkToClipboard,
  getPluralizedWord,
  formateISODateToStr,
} from "@/utils";

import ArrowUpIcon from "@/icons/ArrowUpIcon";
import LockCloseIcon from "@/icons/LockClosedIcon";
import PlusIcon from "@/icons/PlusIcon";
import LinkIcon from "@/icons/LinkIcon";
import InformationIcon from "@/icons/InformationIcon";
import SpeakerphoneIcon from "@/icons/SpeakerphoneIcon";
import EditIcon from "@/icons/EditIcon";
import ChevronDownIcon from "@/icons/ChevronDownIcon";
import TrashIcon from "@/icons/TrashIcon";
import DocumentDuplicateIcon from "@/icons/DocumentDuplicateIcon";
import PaperClipIcon from "@/icons/PaperClipIcon";
import CubIcon from "@/icons/CubIcon";
import DocumentAddIcon from "@/icons/DocumentAddIcon";
import ExclamationCircleIcon from "@/icons/ExclamationCircleIcon";
import DotsHorizontalIcon from "@/icons/DotsHorizontalIcon";
import DownloadIcon from "@/icons/DownloadIcon";

import TaskModal from "@/components/modals/TaskModal";
import TaskTemplatesModal from "@/components/modals/TaskTemplateModal";
import FileModal from "@/components/modals/FileModal";
import TaskInfoModal from "@/components/modals/TaskInfoModal";
import AttachingObjectsToTaskModal from "@/components/modals/AttachingObjectsToTaskModal";
import PublishModal from "@/components/modals/PublishModal";
import CopyTaskModal from "@/components/modals/CopyTaskModal";
import FileLinkModal from "@/components/modals/FileLinkModal";
import PlannedLaborCostsModal from "@/components/modals/PlannedLaborCostsModal";
import ActualLaborCostsModal from "@/components/modals/ActualLaborCostsModal";
import ExportTasksModal from "@/components/modals/ExportTasksModal";

import Breadcrumbs from "@/components/ui/Breadcrumbs";
import MarkdownText, {
  MarkdownEditor,
  normalizeDescriptionMarkdown,
} from "@/components/ui/MarkdownText";
import CopyableText from "@/components/ui/СopyableText";

import SubtasksTab from "./SubtasksTab";
import FilesTab from "./FilesTab";
import InfoSection from "./InfoSection";
import ObjectsTab from "./ObjectsTab";
import HistoryTab from "./HistoryTab";
import ChecklistTab from "./ChecklistTab";
import LaborCostsTab from "./LaborCostsTab";
import PeriodicalSection from "./PeriodicalSection";
import CommentsTab from "./CommentsTab";

import {
  getItem,
  updateItem,
  deleteItem,
  getChildren,
  getTaskFiles,
  getTaskObjects,
  getUserRoles,
  deleteAttachedObject,
  getChecklist,
  addChecklistItem,
  updateChecklistItem,
  deleteChecklistItem,
  checkChecklistItem,
  deleteChecklist,
  getRepeatRules,
  deleteAttachedObjectType,
  getTaskComments,
  addTaskComment,
  addTaskCommentFiles,
  updateTaskComment,
  deleteTaskComment,
  toggleTaskCommentReaction,
} from "@/api/tasks";

import { getImportanceOptions as getPriorities } from "@/api/importances";
import { getTaskTypeOptions as getTaskTypes } from "@/api/taskTypes";
import { getStatusOptions as getStatuses } from "@/api/statuses";
import { getUnitsOptions, getEmployeeUnitsOptions } from "@/api/units";
import { deleteFile } from "@/api/files";
import { getSubscribe, subscribe, unsubscribe } from "@/api/notices";
import { getList as getLogs } from "@/api/systemLogs";
import {
  getList as getLaborCosts,
  deleteItem as deleteLaborCost,
} from "@/api/laborCosts";
import { getLocationText } from "@/api/map";

import {
  DESCRIPTION_EDIT_ROLES,
  EXECUTOR_EDIT_ROLES,
  EXECUTOR_SET_ME_ROLES,
  START_DATE_EDIT_ROLES,
  DUE_DATE_EDIT_ROLES,
  FILES_EDIT_ROLES,
  OBJECTS_EDIT_ROLES,
  COEXECUTOR_EDIT_ROLES,
  ACCEPTOR_EDIT_ROLES,
  ACCEPTOR_SET_ME_ROLES,
  CHECKLIST_EDIT_ROLES,
  CHECKLIST_TOGGLE_ROLES,
  STATUS_OPTIONS,
  EDIT_PLANNED_LABOR_COSTS_ROLES,
  TASK_SPRINT_EDIT_ROLES,
  TASK_EXPORT_ENABLED,
  isTaskStatusFinal,
} from "@/constants/tasks";
import SingleObjectNestedModal from "@/components/modals/SingleObjectNestedModal";

// Базовый стиль подложки Carto Positron
const MAP_STYLE =
  "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json";

const uploadsProps = {
  name: "file",
  action: "/api/file/upload",
  multiple: false,
  showUploadList: false,
};

const getUserUuid = (user = {}) =>
  user.employee_uuid || user.user_uuid || user.uuid;

const getCommentReactionUsers = (reaction = {}) =>
  reaction.users || reaction.reacted_by || [];

const getReactionEmoji = (reaction = {}) => reaction.emoji || reaction.code;

const getCommentReactionUserPayload = (user = {}) => ({
  uuid: getUserUuid(user),
  user_uuid: user.user_uuid,
  employee_uuid: user.employee_uuid,
  firstname: user.firstname,
  lastname: user.lastname,
  patronymic: user.patronymic,
  name: user.name,
  full_name: user.full_name,
  email: user.email,
  avatar: user.avatar,
});

const getTaskDropdownMenuItems = ({
  isReadOnly,
  isMobile = false,
  isUserCanEditFiles,
  handleCreateTemplate,
  handleCopyTask,
  handleDownloadTask,
  handleAddNew,
  onUploadFile,
  handleAddFileLink,
  handleDelete,
  setIsModalAttachedResourcesShow,
  setCurrentAttachedResource,
}) => {
  const readOnlyItems = [
    {
      key: "createTemplate",
      onClick: handleCreateTemplate,
      label: (
        <Flex
          align="center"
          gap={6}
          style={{
            fontSize: 13,
            lineHeight: "21px",
          }}
        >
          <DocumentAddIcon
            style={{
              color: "#BBBBBB",
              fontSize: 16,
            }}
          />
          <span>Создать шаблон по задаче</span>
        </Flex>
      ),
    },
    {
      key: "copyTask",
      onClick: handleCopyTask,
      label: (
        <Flex
          align="center"
          gap={6}
          style={{
            fontSize: 13,
            lineHeight: "21px",
          }}
        >
          <DocumentDuplicateIcon
            style={{
              color: "#BBBBBB",
              fontSize: 16,
            }}
          />
          <span>Создать копию задачи</span>
        </Flex>
      ),
    },
    {
      key: "downloadTask",
      onClick: handleDownloadTask,
      label: (
        <Flex
          align="center"
          gap={6}
          style={{
            fontSize: 13,
            lineHeight: "21px",
          }}
        >
          <DownloadIcon
            style={{
              color: "#BBBBBB",
              fontSize: 16,
            }}
          />
          <span>Скачать задачу</span>
        </Flex>
      ),
    },
  ];

  const visibleReadOnlyItems = TASK_EXPORT_ENABLED
    ? readOnlyItems
    : readOnlyItems.filter(({ key }) => key !== "downloadTask");

  const editableItems = [
    ...visibleReadOnlyItems,
    {
      key: "addTask",
      onClick: handleAddNew,
      label: (
        <Flex
          align="center"
          gap={6}
          style={{
            fontSize: 13,
            lineHeight: "21px",
          }}
        >
          <PlusIcon
            style={{
              color: "#BBBBBB",
              fontSize: 16,
            }}
          />
          <span>Добавить подзадачу</span>
        </Flex>
      ),
    },
    {
      key: "addFile",
      label: (
        <Upload {...uploadsProps} onChange={onUploadFile}>
          <Flex
            align="center"
            gap={6}
            style={{
              fontSize: 13,
              lineHeight: "16px",
              position: "absolute",
              top: "50%",
              transform: "translateY(-50%)",
            }}
          >
            <PaperClipIcon
              style={{
                color: "#BBBBBB",
                fontSize: 16,
              }}
            />
            <span>Добавить вложение</span>
          </Flex>
        </Upload>
      ),
      disabled: !isUserCanEditFiles,
    },
    {
      key: "addSystemFile",
      onClick: handleAddFileLink,
      label: (
        <Flex
          align="center"
          gap={6}
          style={{
            fontSize: 13,
            lineHeight: "21px",
          }}
        >
          <PaperClipIcon
            style={{
              color: "#BBBBBB",
              fontSize: 16,
            }}
          />
          <span>Добавить вложение из системы</span>
        </Flex>
      ),
      disabled: !isUserCanEditFiles,
    },
    {
      key: "addObject",
      label: (
        <Flex
          align="center"
          gap={6}
          style={{
            fontSize: 13,
            lineHeight: "21px",
          }}
        >
          <CubIcon
            style={{
              color: "#BBBBBB",
              fontSize: 16,
            }}
          />
          <span>Прикрепить ресурс</span>
        </Flex>
      ),
      onClick: () => {
        setCurrentAttachedResource(null);
        setIsModalAttachedResourcesShow(true);
      },
    },
    ...(isMobile
      ? [
          {
            type: "divider",
          },
          {
            key: "delete",
            onClick: handleDelete,
            label: (
              <Flex
                align="center"
                gap={6}
                style={{
                  fontSize: 13,
                  lineHeight: "21px",
                }}
              >
                <TrashIcon
                  style={{
                    color: "#BBBBBB",
                    fontSize: 16,
                  }}
                />
                <span>Удалить задачу</span>
              </Flex>
            ),
          },
        ]
      : []),
  ];

  return isReadOnly ? visibleReadOnlyItems : editableItems;
};

const Task = ({ isAdmins = false }) => {
  const { id, unit_uuid } = useParams();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(UserContext);
  const { setLayoutPending } = useContext(MainContext);
  const handledCommentHashRef = useRef(null);

  const [data, setData] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [isDescriptionEditing, setIsDescriptionEditing] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState("");
  const [isDescriptionSaving, setIsDescriptionSaving] = useState(false);
  const [pendingDescriptionNavigation, setPendingDescriptionNavigation] =
    useState(null);

  const [repeatRules, setRepeatRules] = useState({});

  const [activeTab, setActiveTab] = useState("subtasks");

  const [statuses, setStatuses] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [units, setUnits] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);

  const [status, setStatus] = useState();
  const [priority, setPriority] = useState();
  const [taskType, setTaskType] = useState();

  const [subscribeStatus, setSubscribeStatus] = useState(false);
  const [subscribeLoading, setSubscribeLoading] = useState(false);

  // Окно публикации
  const [isPublishModalShow, setIsPublishModalShow] = useState(false);

  // Окно копирования
  const [isCopyModalShow, setIsCopyModalShow] = useState(false);
  const [subtaskCopyData, setSubtaskCopyData] = useState(null);

  // Окно создания шаблона
  const [isTemplateModalShow, setIsTemplateModalShow] = useState(false);

  // Окно загрузки файла из системы
  const [isSystemModalShow, setIsSystemModalShow] = useState(false);

  const is_overdue = useMemo(() => {
    if (!data) return false;
    return data.due_date && new Date() >= new Date(data.due_date);
  }, [data]);

  const [children, setChildren] = useState([]);
  const [showDeletedSubtasks, setShowDeletedSubtasks] = useState(false);
  const [files, setFiles] = useState([]);
  const [objects, setObjects] = useState([]);
  const objectsCount = useMemo(
    () =>
      objects.reduce(
        (total, { objects: attachedObjects = [] }) =>
          total +
          attachedObjects.reduce(
            (count, object) =>
              count + 1 + (object.nested_resources?.length || 0),
            0,
          ),
        0,
      ),
    [objects],
  );
  const [laborCosts, setLaborCosts] = useState([]);
  const [laborCostsPagination, setLaborCostsPagination] = useState({});
  const [logs, setLogs] = useState([]);
  const [logsPagination, setLogsPagination] = useState({});
  const [checklist, setChecklist] = useState([]);
  const [comments, setComments] = useState([]);

  const [childrenPending, setChildrenPending] = useState(true);
  const [filesPending, setFilesPending] = useState(true);
  const [objectsPending, setObjectsPending] = useState(true);
  const [laborCostsPending, setLaborCostsPending] = useState(true);
  const [logsPending, setLogsPending] = useState(true);
  const [checklistPending, setChecklistPending] = useState(true);
  const [commentsPending, setCommentsPending] = useState(true);

  const isExecutor = user?.employee_uuid === data?.executor?.uuid;
  const isAcceptor = user?.employee_uuid === data?.assignee_acceptor?.uuid;

  const [isModalShow, setIsModalShow] = useState(false);
  const [modalItem, setModalItem] = useState();

  const [isModalFileShow, setIsModalFileShow] = useState(false);
  const [currentFile, setCurrentFile] = useState();

  const [isModalInfoShow, setIsModalInfoShow] = useState(false);

  // Модальное окно для прикрепленных ресурсов
  const [isModalAttachedResourcesShow, setIsModalAttachedResourcesShow] =
    useState(false);
  const [currentAttachedResource, setCurrentAttachedResource] = useState(null);
  const [isModalNestedResourcesShow, setIsModalNestedResourcesShow] =
    useState(false);

  const [
    isModalEditPlannedLaborCostsShow,
    setIsModalEditPlannedLaborCostsShow,
  ] = useState(false);
  const [isModalActualLaborCostsShow, setIsModalActualLaborCostsShow] =
    useState(false);
  const [currentLaborCost, setCurrentLaborCost] = useState(null);
  const [currentTask, setCurrentTask] = useState(null);

  const isDeleted = useMemo(() => {
    const delStatus = statuses.find(({ is_for_deletion }) => is_for_deletion);
    return data?.status_uuid === delStatus?.uuid;
  }, [data, statuses]);

  const isEnded = useMemo(() => {
    const status =
      statuses.find(({ uuid }) => uuid === data?.status_uuid) ||
      data?.status;

    return isTaskStatusFinal(status);
  }, [data?.status, data?.status_uuid, statuses]);

  const draftStatus = useMemo(() => {
    const status = statuses.find(({ is_for_draft }) => is_for_draft);
    const icon = STATUS_OPTIONS.find(
      ({ value }) => value === status?.color,
    )?.icon;

    return { ...status, icon };
  }, [statuses]);

  const isDraft = useMemo(() => {
    return data?.status_uuid === draftStatus?.uuid;
  }, [data, draftStatus]);

  // Флаг, является ли родительская задача черновиком
  const isParentDraft = useMemo(() => {
    return data?.parent?.status_uuid === draftStatus?.uuid;
  }, [data, draftStatus]);

  const [userRoles, setUserRoles] = useState([]);
  const isUserCanEditDescription = useMemo(() => {
    return (
      !isDeleted &&
      !isEnded &&
      !data?.readOnly &&
      userRoles.some((role) => DESCRIPTION_EDIT_ROLES.includes(role))
    );
  }, [userRoles, data, isDeleted, isEnded]);
  const hasDescriptionDraftChanges = useMemo(() => {
    if (!isDescriptionEditing) return false;

    return (
      normalizeDescriptionMarkdown(descriptionDraft) !==
      normalizeDescriptionMarkdown(data?.description)
    );
  }, [data?.description, descriptionDraft, isDescriptionEditing]);
  const isUserCanEditExecutor = useMemo(() => {
    return (
      !isDeleted &&
      !isEnded &&
      !data?.readOnly &&
      userRoles.some((role) => EXECUTOR_EDIT_ROLES.includes(role))
    );
  }, [userRoles, data, isDeleted, isEnded]);
  const isUserSetMeExecutor = useMemo(() => {
    return (
      !isDeleted &&
      !isEnded &&
      !data?.readOnly &&
      userRoles.some((role) => EXECUTOR_SET_ME_ROLES.includes(role))
    );
  }, [userRoles, data, isDeleted, isEnded]);
  const isUserCanEditAcceptor = useMemo(() => {
    return (
      !isDeleted &&
      !isEnded &&
      !data?.readOnly &&
      userRoles.some((role) => ACCEPTOR_EDIT_ROLES.includes(role))
    );
  }, [userRoles, data, isDeleted, isEnded]);
  const isUserSetMeAcceptor = useMemo(() => {
    return (
      !isDeleted &&
      !isEnded &&
      !data?.readOnly &&
      userRoles.some((role) => ACCEPTOR_SET_ME_ROLES.includes(role))
    );
  }, [userRoles, data, isDeleted, isEnded]);

  const isUserCanEditStartDate = useMemo(() => {
    return (
      !isDeleted &&
      !isEnded &&
      !data?.readOnly &&
      userRoles.some((role) => START_DATE_EDIT_ROLES.includes(role))
    );
  }, [userRoles, data, isDeleted, isEnded]);
  const isUserCanEditDueDate = useMemo(() => {
    return (
      !isDeleted &&
      !isEnded &&
      !data?.readOnly &&
      userRoles.some((role) => DUE_DATE_EDIT_ROLES.includes(role))
    );
  }, [userRoles, data, isDeleted, isEnded]);
  const isUserCanEditFiles = useMemo(() => {
    return (
      !isDeleted &&
      !isEnded &&
      !data?.readOnly &&
      userRoles.some((role) => FILES_EDIT_ROLES.includes(role))
    );
  }, [userRoles, data, isDeleted, isEnded]);
  const isUserCanEditObjectsList = useMemo(() => {
    return (
      !isDeleted &&
      !isEnded &&
      !data?.readOnly &&
      userRoles.some((role) => OBJECTS_EDIT_ROLES.includes(role))
    );
  }, [userRoles, data, isDeleted, isEnded]);
  const isUserCanEditCoexecutors = useMemo(() => {
    return (
      !isDeleted &&
      !isEnded &&
      !data?.readOnly &&
      userRoles.some((role) => COEXECUTOR_EDIT_ROLES.includes(role))
    );
  }, [userRoles, data, isDeleted, isEnded]);

  const isUserCanEditSprint = useMemo(() => {
    return (
      !isDeleted &&
      !isEnded &&
      !data?.readOnly &&
      userRoles.some((role) => TASK_SPRINT_EDIT_ROLES.includes(role))
    );
  }, [userRoles, data, isDeleted, isEnded]);

  const showSetMeExecutor = useMemo(() => {
    const access = !isExecutor && isUserSetMeExecutor;
    if (!access) return false;

    // Если можно назначить себя исполнителем, но нельзя редактировать исполнителя, даем доступ только если поле пустое
    if (isUserSetMeExecutor && !isUserCanEditExecutor) {
      return !data.responsible_executor_uuid;
    } else {
      return access;
    }
  }, [isExecutor, data, isUserSetMeExecutor, isUserCanEditExecutor]);

  const showSetMeAcceptor = useMemo(() => {
    const access = !isAcceptor && isUserSetMeAcceptor;
    if (!access) return false;

    // Если можно назначить себя приемщиком, но нельзя редактировать приемщика, даем доступ только если поле пустое
    if (isUserSetMeAcceptor && !isUserCanEditAcceptor) {
      return !data.responsible_acceptor_uuid;
    } else {
      return access;
    }
  }, [isAcceptor, data, isUserSetMeAcceptor, isUserCanEditAcceptor]);

  const isUserCanEditChecklist = useMemo(() => {
    return (
      !isDeleted &&
      !isEnded &&
      !data?.readOnly &&
      userRoles.some((role) => CHECKLIST_EDIT_ROLES.includes(role))
    );
  }, [userRoles, data, isDeleted, isEnded]);

  const isUserCanToggleChecklist = useMemo(() => {
    return (
      !isDeleted &&
      !isEnded &&
      !data?.readOnly &&
      userRoles.some((role) => CHECKLIST_TOGGLE_ROLES.includes(role))
    );
  }, [userRoles, data, isDeleted, isEnded]);

  const isUserCanEditPlannedLaborCosts = useMemo(() => {
    return (
      !isDeleted &&
      !isEnded &&
      !data?.readOnly &&
      userRoles.some((role) => EDIT_PLANNED_LABOR_COSTS_ROLES.includes(role))
    );
  }, [userRoles, data, isDeleted, isEnded]);

  const [address, setAddress] = useState("");

  const targetCommentUuid = useMemo(() => {
    const match = /^#comments\/([^/]+)$/.exec(location.hash);
    return match ? decodeURIComponent(match[1]) : null;
  }, [location.hash]);

  // initial fetch
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    if (location.hash === "#comments" || targetCommentUuid) {
      setActiveTab("comments");
    }
  }, [location.hash, targetCommentUuid]);

  useEffect(() => {
    if (!targetCommentUuid || activeTab !== "comments" || commentsPending) return;
    if (handledCommentHashRef.current === location.hash) return;

    window.requestAnimationFrame(() => {
      const didScroll = scrollToComment(targetCommentUuid);

      if (didScroll) {
        handledCommentHashRef.current = location.hash;
      }
    });
  }, [targetCommentUuid, activeTab, commentsPending, comments, location.hash]);

  const scrollToComment = (uuid) => {
    const element = document.getElementById(`comment-${uuid}`);
    if (!element) return false;

    element.scrollIntoView({ behavior: "smooth", block: "center" });
    element.classList.remove("comment-anchor-highlight");

    window.requestAnimationFrame(() => {
      element.classList.add("comment-anchor-highlight");

      window.setTimeout(() => {
        element.classList.remove("comment-anchor-highlight");
      }, 1800);

      if (window.location.hash === `#comments/${uuid}`) {
        window.history.replaceState(
          null,
          "",
          `${window.location.pathname}${window.location.search}#comments`,
        );
      }
    });

    return true;
  };

  const fetchData = async () => {
    setIsLoading(true);
    setIsDescriptionEditing(false);
    setDescriptionDraft("");

    try {
      const [data, { units }, statuses, priorities, roles, rules, taskTypes] =
        await Promise.all([
          getItem(id, isAdmins),
          isAdmins ? getUnitsOptions() : getEmployeeUnitsOptions(),
          getStatuses(),
          getPriorities(),
          getUserRoles(id),
          getRepeatRules(id),
          getTaskTypes(),
        ]);

      setData(data);
      setDescriptionDraft(normalizeDescriptionMarkdown(data?.description));
      setStatuses(statuses);
      setPriorities(priorities);
      setTaskTypes(taskTypes);
      setUnits(units);
      setStatus(statuses.find((status) => status.uuid === data.status_uuid));
      setPriority(
        priorities.find((priority) => priority.uuid === data.importance_uuid),
      );
      setTaskType(taskTypes.find((type) => type.uuid === data.task_type_uuid));
      setUserRoles(roles);
      setRepeatRules({
        ...rules,
        ...(rules?.start_date && { start_date: dayjs(rules.start_date) }),
        ...(rules?.end_date && { end_date: dayjs(rules.end_date) }),
      });

      fetchChildren();
      fetchFiles();
      fetchObjects();
      fetchLogs();
      fetchLaborCosts();
      fetchChecklist();
      fetchComments();
      fetchAddress(data.location);

      const isTaskSubscribe = await getSubscribe({
        user: user.user_uuid,
        uuid: id,
      });
      setSubscribeStatus(isTaskSubscribe.is_subscribed);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchChildren = async (
    shouldShowDeleted = showDeletedSubtasks,
  ) => {
    setChildrenPending(true);
    try {
      const children = await getChildren(id, isAdmins, shouldShowDeleted);
      setChildren(children);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setChildrenPending(false);
    }
  };

  const handleShowDeletedSubtasksChange = (checked) => {
    setShowDeletedSubtasks(checked);
    fetchChildren(checked);
  };

  const fetchFiles = async () => {
    setFilesPending(true);
    try {
      const { files } = await getTaskFiles({ uuid: id }, isAdmins);

      setFiles(files);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setFilesPending(false);
    }
  };

  const fetchObjects = async () => {
    setObjectsPending(true);
    try {
      const objects = await getTaskObjects({ uuid: id }, isAdmins);

      setObjects(objects);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setObjectsPending(false);
    }
  };

  const fetchLaborCosts = async (page = 1, pageSize) => {
    setLaborCostsPending(true);
    try {
      const params = { uuid: id, page };

      if (pageSize) {
        params.perPage = pageSize;
      }

      const laborCosts = await getLaborCosts(params);

      setLaborCosts(laborCosts.timeEntries);
      setLaborCostsPagination({
        total: laborCosts.total,
        current: laborCosts.page,
        pageSize: laborCosts.perPage,
      });
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setLaborCostsPending(false);
    }
  };

  const handleLaborCostsTableChange = (pagination) => {
    fetchLaborCosts(pagination.current, pagination.pageSize);
  };

  const fetchLogs = async (page = 1, pageSize) => {
    setLogsPending(true);

    try {
      const params = { entity_id: id, page };

      if (pageSize) {
        params.perPage = pageSize;
      }

      const logs = await getLogs(params, isAdmins);
      setLogs(logs.logs);
      setLogsPagination({
        total: logs.total,
        current: logs.page,
        pageSize: logs.perPage,
      });
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setLogsPending(false);
    }
  };

  const handleHistoryTableChange = (pagination) => {
    fetchLogs(pagination.current, pagination.pageSize);
  };

  const fetchChecklist = async () => {
    setChecklistPending(true);
    try {
      const checklist = await getChecklist(id);
      setChecklist(checklist);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setChecklistPending(false);
    }
  };

  const fetchComments = async ({
    showPending = true,
    showError = true,
    rethrow = false,
  } = {}) => {
    if (showPending) {
      setCommentsPending(true);
    }

    try {
      const comments = await getTaskComments({ task_uuid: id, sort: "asc" });
      setComments(comments);
    } catch (error) {
      if (showError) {
        message.open({
          type: "error",
          content: error.message,
          duration: 10,
        });
      }

      if (rethrow) {
        throw error;
      }
    } finally {
      if (showPending) {
        setCommentsPending(false);
      }
    }
  };

  const fetchAddress = async (location) => {
    if (!location?.x || !location?.y) {
      setAddress("");
      return;
    }

    getLocationText({ latitude: location.x, longitude: location.y }).then(
      (text) => {
        setAddress(text);
      },
    );
  };

  // После сохранения в модалке задачи (может быть подзадачей)
  const handleSaveItem = (item) => {
    setIsModalShow(false);
    if (item.uuid === data.uuid) fetchData();
    else {
      setActiveTab("subtasks");
      fetchChildren();
    }
  };

  const setMeExecutor = async () => {
    const res = { ...data };
    res.responsible_executor_uuid = user.employee_uuid;
    setIsLoading(true);

    try {
      await updateItem(res);
      await fetchData();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const setMeAcceptor = async () => {
    const res = { ...data };
    res.assignee_acceptor_uuid = user.employee_uuid;
    setIsLoading(true);

    try {
      await updateItem(res);
      await fetchData();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const setIsGroupTask = async (isGroupTask) => {
    setIsLoading(true);
    const res = { ...data, is_accessible_for_all_in_unit: isGroupTask };

    try {
      await updateItem(res);
      await fetchData();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Удаление задачи или подзадачи
  const handleDelete = (data) => {
    const currentIsDraft = data?.status_uuid === draftStatus?.uuid;

    Modal.confirm({
      title: currentIsDraft
        ? `Вы уверены, что хотите удалить черновик ${data.name}?`
        : `Вы уверены, что хотите удалить задачу ${data.name}?`,
      content: currentIsDraft ? (
        <span>
          Черновик будет полностью удален из&nbsp;системы. Это&nbsp;действие
          нельзя будет отменить. Прикрепленные файлы, ресурсы и&nbsp;подзадачи
          останутся в&nbsp;системе.
        </span>
      ) : (
        "Задача будет переведена в статус 'Удалена'"
      ),

      onOk() {
        handleConfirmDelete(data);
      },
      okText: "Удалить",
      cancelText: "Отмена",
    });
  };

  // Действительно удаляем задачу после подтверждения
  const handleConfirmDelete = async (data) => {
    try {
      await deleteItem({ uuid: data.uuid });

      if (data.uuid === id) {
        isAdmins
          ? navigate(routes.administration.tasks)
          : unit_uuid
            ? navigate(routes.unitTasks(unit_uuid))
            : navigate(routes.tasks);
      } else {
        fetchChildren();
      }
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    }
  };

  // Создать шаблон по задаче
  const handleCreateTemplate = () => {
    setIsTemplateModalShow(true);
  };

  const handleSaveTemplate = () => {
    setIsTemplateModalShow(false);
  };
  // Копирование задачи
  const handleCopyTask = () => {
    setIsCopyModalShow(true);
  };

  const handleSaveCopyItem = (txUuid) => {
    setIsCopyModalShow(false);
    setSubtaskCopyData(null);

    const params = new URLSearchParams({
      copy_transaction_id: txUuid,
    }).toString();
    const allTasksPath = isAdmins ? routes.administration.tasks : routes.tasks;

    navigate({ pathname: allTasksPath, search: `?${params}` });
  };

  const [isDownloadTaskModalShow, setIsDownloadTaskModalShow] = useState(false);

  const handleDownloadTask = () => {
    if (!TASK_EXPORT_ENABLED) return;

    setIsDownloadTaskModalShow(true);
  };

  const handleExportTask = ({ folder_uuid, uuid, original_name } = {}) => {
    setIsDownloadTaskModalShow(false);

    if (uuid) {
      const link = document.createElement("a");
      link.href = `/api/file/download?uuid=${uuid}`;
      link.download = original_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      notification.success({
        message: "Экспорт успешно завершен",
        duration: 10,
        showProgress: true,
        pauseOnHover: true,
        style: {
          width: 480,
        },
        description: folder_uuid ? (
          <p>
            Файл сохранен в личную рабочую группу в папку{" "}
            <a href={routes.folder(folder_uuid)}>Выгрузка</a>.
          </p>
        ) : null,
      });
    }
  };

  // Добавление новой подзадачи
  const handleAddNew = async () => {
    setModalItem({
      parent_task_uuid: data.uuid,
      parent: { ...data },
      unit_uuid: data.unit_uuid, // наследует РГ от родителя
    });
    setIsModalShow(true);
    fetchLogs();
  };

  // Открыть модалку для редактирования текущей задачи
  const handleEditTask = () => {
    if (data?.readOnly || isDeleted || isEnded) return;

    setModalItem({
      ...data,
      roles: userRoles,
      has_children: !!children.length,
    });
    setIsModalShow(true);
  };

  const handleEditDescription = () => {
    if (!isUserCanEditDescription) return;

    setDescriptionDraft(normalizeDescriptionMarkdown(data?.description));
    setIsDescriptionEditing(true);
  };

  const handleCancelDescriptionEdit = () => {
    setDescriptionDraft(normalizeDescriptionMarkdown(data?.description));
    setIsDescriptionEditing(false);
  };

  const saveDescriptionDraft = async ({ refetch = true } = {}) => {
    if (!isUserCanEditDescription) return;

    setIsDescriptionSaving(true);

    try {
      const description = normalizeDescriptionMarkdown(descriptionDraft);
      const updatedTask = await updateItem({
        ...data,
        description,
      });
      const nextData = updatedTask || {
        ...data,
        description,
      };

      setData(nextData);
      setDescriptionDraft(normalizeDescriptionMarkdown(nextData.description));
      setIsDescriptionEditing(false);
      if (refetch) {
        fetchData();
      }
      return true;
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
      return false;
    } finally {
      setIsDescriptionSaving(false);
    }
  };

  const handleSaveDescription = async () => {
    await saveDescriptionDraft();
  };

  const handleBreadcrumbNavigate = (href, event) => {
    if (!hasDescriptionDraftChanges) return;

    event.preventDefault();
    setPendingDescriptionNavigation(href);
  };

  const handleStayOnTask = () => {
    setPendingDescriptionNavigation(null);
  };

  const handleDiscardDescriptionAndNavigate = () => {
    const href = pendingDescriptionNavigation;

    setPendingDescriptionNavigation(null);
    setDescriptionDraft(normalizeDescriptionMarkdown(data?.description));
    setIsDescriptionEditing(false);

    if (href) {
      navigate(href);
    }
  };

  const handleSaveDescriptionAndNavigate = async () => {
    const href = pendingDescriptionNavigation;
    const isSaved = await saveDescriptionDraft({ refetch: false });

    if (!isSaved) return;

    setPendingDescriptionNavigation(null);
    if (href) {
      navigate(href);
    }
  };

  // Открыть модалку для редактирования подзадачи
  const handleEditSubtask = (item) => {
    const status =
      statuses.find(({ uuid }) => uuid === item?.status_uuid) || item?.status;

    if (item?.readOnly || isTaskStatusFinal(status)) return;

    setModalItem(item);
    setIsModalShow(true);
  };

  // Открыть модалку для копирования подзадачи
  const handleCopySubtask = (item) => {
    setSubtaskCopyData(item);
    setIsCopyModalShow(true);
  };

  // Открыть модалку для редактирования фактической трудозатраты
  const handleEditLaborCost = (item) => {
    setCurrentLaborCost(item);
    setIsModalActualLaborCostsShow(true);
  };

  // Удаление фактической трудозатраты
  const handleDeleteLaborCost = (item) => {
    const { hours, recorded_at } = item;
    Modal.confirm({
      title: `Вы уверены, что хотите удалить запись: ${hours} ${getPluralizedWord(
        hours,
        ["час", "часа", "часов"],
      )} за ${formateISODateToStr(recorded_at)}?`,
      content: "Это действие нельзя будет отменить. Данные будут удалены.",
      onOk() {
        handleConfirmDeleteLaborCost(item);
      },
      okText: "Удалить",
      cancelText: "Отмена",
    });
  };

  // Действительно удаляем трудозатрату после подтверждения
  const handleConfirmDeleteLaborCost = async ({ uuid }) => {
    setIsLoading(true);
    try {
      await deleteLaborCost({ uuid });

      await fetchData();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
      setIsLoading(false);
    }
  };

  // После сохранения закрыть модалку и обновить фактические трудозатраты
  const onLaborCostSave = () => {
    setActiveTab("laborCosts");
    setIsModalActualLaborCostsShow(false);
    setCurrentTask(null);
    fetchData();
  };

  // Открыть модалку для редактирования прикрепленного файла
  const handleEditFile = (item) => {
    setCurrentFile(item);
    setIsModalFileShow(true);
  };

  // После сохранения изменений в файле, закрыть модалку и обновить список файлов
  const onFileModalSave = () => {
    setActiveTab("files");
    setIsModalFileShow(false);
    fetchFiles();
    fetchLogs();
  };

  // Удаление файла
  const handleDeleteFile = (item) => {
    const { name } = item;
    const isAttachedFromComment = Boolean(item.attached_from_comment);

    Modal.confirm({
      title: isAttachedFromComment
        ? "Этот файл прикреплен к комментарию, вы действительно хотите его удалить?"
        : `Вы уверены, что хотите удалить файл ${name} ?`,
      content: isAttachedFromComment
        ? "Файл удалится из вложений задачи и связанного комментария. Это действие нельзя будет отменить."
        : "Это действие нельзя будет отменить. Данные будут удалены.",
      onOk() {
        handleConfirmDeleteFile(item);
      },
      okText: "Удалить",
      cancelText: "Отмена",
      okButtonProps: { danger: true },
    });
  };

  // Действительно удаляем файл после подтверждения
  const handleConfirmDeleteFile = async (item) => {
    const { uuid } = item;
    setFilesPending(true);
    try {
      await deleteFile({ uuid });

      await fetchFiles();
      if (item.attached_from_comment) {
        fetchComments({ showPending: false });
      }
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
      setFilesPending(false);
    }
  };

  // Обработчик публикации черновика
  const onPublishDraft = (item) => {
    setModalItem(item);
    setIsPublishModalShow(true);
  };

  // Обработчик закрытия модалки публикации
  const handlePublishSave = () => {
    setIsPublishModalShow(false);
    fetchData();
  };

  // Обработчик загрузки файла, открывает 2 шаг прикрепления файла
  const onUploadFile = ({ file }) => {
    const { status } = file;

    if (file.status === "uploading") {
      setLayoutPending(true);
      return;
    }

    if (status === "done") {
      setLayoutPending(false);
      const fileObj = file.response.files[0];
      const name = fileObj.original_name.split(".").slice(0, -1).join(".");
      setCurrentFile({
        ...fileObj,
        name,
        folder_uuid: data.folder_uuid || data.task_number,
        unit_uuid: data.unit_uuid,
        folder: {
          uuid: data.task_number,
          label: data.task_number,
        },
        task_uuid: data.uuid,
        additional: true,
        isSystem: false,
      });
      setIsModalFileShow(true);
    } else if (status === "error") {
      setLayoutPending(false);

      message.error(
        `Загрузка файла ${file.name} не удалась. Попробуйте еще раз.`,
      );
    }
  };

  const handleAddFileLink = () => {
    setIsSystemModalShow(true);
  };

  const handleSaveFileLink = async (payload) => {
    const name =
      (payload.name || payload.original_name || "Файл из системы")
        .split(".")
        .slice(0, -1)
        .join(".") ||
      payload.name ||
      payload.original_name ||
      "Файл из системы";

    setCurrentFile({
      ...payload,
      name,
      folder_uuid: data.folder_uuid || data.task_number,
      unit_uuid: data.unit_uuid,
      folder: {
        uuid: data.task_number,
        label: data.task_number,
      },
      task_uuid: data.uuid,
      additional: true,
      isSystem: true,
    });

    setActiveTab("files");
    setIsModalFileShow(true);
    setIsSystemModalShow(false);
  };

  const onAddNewObject = () => {
    setActiveTab("resources");
    setIsModalAttachedResourcesShow(false);
    setIsModalNestedResourcesShow(false);
    fetchObjects();
    fetchLogs();
  };

  const handleDeleteObject = (data, isType = false) => {
    const content = data.uuid && !isType
      ? data.nested_resources?.length > 0
        ? "Ресурс и связанные с ним ресурсы будут откреплены от задачи, но останутся в списке ресурсов"
        : "Ресурс будет откреплен от задачи, но останется в списке ресурсов"
      : "Тип ресурса будет откреплен от задачи, но останется в списке ресурсов";
    Modal.confirm({
      title: `Вы уверены, что хотите открепить ${data.uuid && !isType ? "ресурс" : "тип ресурса"} ${data.uuid && !isType ? data.name : data.type?.definition?.name}?`,
      content: content,
      onOk() {
        handleConfirmDeleteObject(data, isType);
      },
      okText: "Удалить",
      cancelText: "Отмена",
    });
  };

  const handleConfirmDeleteObject = async (data, isType = false) => {
    setObjectsPending(true);
    const isObject = data.uuid && !isType;
    try {
      if (isObject) {
        await deleteAttachedObject({
          task_object_uuid: data.uuid,
        });
      } else {
        await deleteAttachedObjectType({
          task_uuid: id,
          uuid: data.type?.uuid,
        });
      }

      fetchObjects();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setObjectsPending(false);
    }
  };

  const handleSavePlannedCosts = async (data) => {
    setLaborCostsPending(true);

    try {
      const item = {
        uuid: id,
        ...data,
      };
      await updateItem(item);

      fetchData();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    }

    setLaborCostsPending(false);
    setIsModalEditPlannedLaborCostsShow(false);
    setCurrentTask(null);
  };

  // Подписка
  const handleSubscribe = async () => {
    setSubscribeLoading(true);
    try {
      const item = {
        type: "task",
        items: [id],
        user: user.user_uuid,
      };

      if (subscribeStatus) {
        await unsubscribe(item);
      } else {
        await subscribe(item);
      }

      fetchData();
      showNotice(!subscribeStatus);
    } catch (error) {
      message.open({ type: "error", content: error.message, duration: 10 });
    }
    setSubscribeLoading(false);
  };

  const showNotice = (isSubscribed) => {
    const style = isSubscribed
      ? {
          border: "1px solid #B7EB8F",
          background: "#f6ffed",
          borderRadius: "8px",
          width: "500px",
        }
      : {
          border: "1px solid #91CAFF",
          background: "#e6f4ff",
          borderRadius: "8px",
          width: "500px",
        };

    const taskLabel = data?.parent?.task_number
      ? `Задача №${data.parent.task_number}`
      : `Задача №${data?.task_number}`;

    notification.open({
      message: isSubscribed
        ? `Подписка добавлена: ${taskLabel}`
        : `Подписка отменена: ${taskLabel}`,
      description: isSubscribed
        ? "Вы подписались на объект раздела. Все события, связанные с этим объектом, будут у вас в уведомлениях."
        : "Вы отписались от уведомлений на объект раздела.",
      icon: null,
      duration: 4,
      pauseOnHover: true,
      placement: "bottomRight",
      style,
    });
  };

  // Чеклист
  const handleEditChecklistItem = async (item) => {
    setChecklistPending(true);

    try {
      if (item.uuid) {
        await updateChecklistItem(item);
      } else {
        // Новый пункт, добавляем
        await addChecklistItem({ task_uuid: id, title: item.title });
      }
      await fetchChecklist();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setChecklistPending(false);
    }
  };

  const handleCheckChecklistItem = async (item) => {
    setChecklistPending(true);
    try {
      await checkChecklistItem(item);
      await fetchChecklist();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setChecklistPending(false);
    }
  };

  const handleDeleteChecklistItem = async (uuid) => {
    setChecklistPending(true);
    try {
      await deleteChecklistItem(uuid);
      await fetchChecklist();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setChecklistPending(false);
    }
  };

  const handleDeleteList = async () => {
    setChecklistPending(true);
    try {
      await deleteChecklist(id);
      await fetchChecklist();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setChecklistPending(false);
    }
  };

  const handleCreateComment = async (
    text,
    replyToComment,
    attachments = [],
  ) => {
    try {
      await addTaskComment({
        task_uuid: id,
        body: text,
        quoted_comment_uuid: replyToComment?.uuid,
        attachments,
      });
      setActiveTab("comments");
      await fetchComments({ showPending: false });
      fetchFiles();
      fetchLogs();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
      throw error;
    }
  };

  const handleUpdateComment = async (uuid, text, attachments = []) => {
    try {
      await updateTaskComment({
        uuid,
        body: text,
      });

      if (attachments.length) {
        await addTaskCommentFiles({
          comment_uuid: uuid,
          attachments,
        });
      }

      await fetchComments({ showPending: false });
      fetchFiles();
      fetchLogs();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
      throw error;
    }
  };

  const handleDeleteComment = async (uuid) => {
    try {
      await deleteTaskComment({ uuid });
      await fetchComments();
      fetchLogs();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
      throw error;
    }
  };

  const toggleCommentReactionOptimistically = (commentUuid, emoji) => {
    const currentUserUuid = getUserUuid(user);
    if (!currentUserUuid) return;

    const currentUserReactionPayload = getCommentReactionUserPayload(user);

    setComments((currentComments) =>
      currentComments.map((comment) => {
        if (comment.uuid !== commentUuid) return comment;

        const reactions = comment.reactions || [];
        const existingReaction = reactions.find(
          (reaction) => getReactionEmoji(reaction) === emoji,
        );
        const nextReactions = existingReaction
          ? reactions
              .map((reaction) => {
                if (getReactionEmoji(reaction) !== emoji) return reaction;

                const users = getCommentReactionUsers(reaction);
                const isSelected = users.some(
                  (reactionUser) =>
                    getUserUuid(reactionUser) === currentUserUuid,
                );
                const nextUsers = isSelected
                  ? users.filter(
                      (reactionUser) =>
                        getUserUuid(reactionUser) !== currentUserUuid,
                    )
                  : [...users, currentUserReactionPayload];

                return {
                  ...reaction,
                  emoji,
                  code: emoji,
                  users: nextUsers,
                  count: nextUsers.length,
                };
              })
              .filter((reaction) => (reaction.count ?? 0) > 0)
          : [
              ...reactions,
              {
                emoji,
                code: emoji,
                users: [currentUserReactionPayload],
                count: 1,
              },
            ];

        return {
          ...comment,
          reactions: nextReactions,
        };
      }),
    );
  };

  const handleToggleCommentReaction = async (commentUuid, emoji) => {
    toggleCommentReactionOptimistically(commentUuid, emoji);

    try {
      await toggleTaskCommentReaction({
        comment_uuid: commentUuid,
        emoji,
      });
      await fetchComments({ showPending: false });
    } catch (error) {
      await fetchComments({ showPending: false, showError: false }).catch(
        () => {},
      );
      message.open({
        type: "warning",
        content:
          "Не удалось поставить реакцию",
        duration: 4,
      });
    }
  };

  const handleDeleteCommentAttachment = async (_, fileUuid) => {
    try {
      await deleteFile({ uuid: fileUuid });
      await fetchComments();
      fetchFiles();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
      throw error;
    }
  };

  const handleNavigateToComment = (uuid) => {
    handledCommentHashRef.current = `#comments/${uuid}`;
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${window.location.search}#comments/${uuid}`,
    );
    setActiveTab("comments");

    window.requestAnimationFrame(() => {
      scrollToComment(uuid);
    });
  };

  const handleCopyCommentLink = async (uuid) => {
    const result = await copyLinkToClipboard(
      `${window.location.origin}${window.location.pathname}${window.location.search}#comments/${uuid}`,
    );

    message.open({
      type: result.success ? "success" : "error",
      content: result.message,
      duration: 3,
    });
  };

  return (
    <>
      {isLoading ? (
        <div className="spin">
          <Spin size="large" />
        </div>
      ) : (
        data && (
          <>
            <Flex vertical gap={25}>
              <Flex vertical gap={10}>
                <Breadcrumbs
                  onNavigate={handleBreadcrumbNavigate}
                  items={
                    isAdmins
                      ? [
                          { title: "Административная панель" },
                          {
                            title: "Задачи",
                          },
                          {
                            title: "Список задач",
                            href: routes.administration.tasks,
                          },
                          data?.parent && data.parent.task_number
                            ? {
                                title: `Задача №${data.parent.task_number}`,

                                href: routes.administration.taskDetail(
                                  data.parent.uuid,
                                ),
                              }
                            : {},
                          {
                            title: `Задача №${data.task_number}`,
                          },
                        ]
                      : [
                          {
                            title: "Главная",
                            href: routes.dashboards,
                          },
                          unit_uuid ? { title: data?.unit?.name } : {},
                          {
                            title: "Задачи",
                          },
                          {
                            title: "Список задач",
                            href: unit_uuid
                              ? routes.unitTasks(data.unit_uuid)
                              : routes.tasks,
                          },
                          data?.parent && data.parent.task_number
                            ? {
                                title: `Задача №${data.parent.task_number}`,

                                href: unit_uuid
                                  ? routes.unitTaskDetail(
                                      data.parent.unit_uuid,
                                      data.parent.uuid,
                                    )
                                  : routes.taskDetail(data.parent.uuid),
                              }
                            : {},
                          {
                            title: `Задача №${data.task_number}`,
                          },
                        ]
                  }
                />
                <Flex align="flex-start" gap={20} justify="space-between">
                  <Flex align="center" gap={8} wrap="wrap">
                    <Typography.Title level={2} className="no-margin">
                      {data?.parent_task_uuid ? "Подзадача" : "Задача"}{" "}
                      {data.task_number}
                    </Typography.Title>
                    {data?.parent_task_uuid && data.parent && (
                      <Tag
                        icon={<ArrowUpIcon />}
                        color="blue"
                        style={{
                          color: "#367AF0",
                          lineHeight: "20px",
                          fontSize: 12,
                          backgroundColor: "#F0F6FF",
                          borderColor: "#D4E4FF",
                        }}
                      >
                        Задача {data.parent.task_number}
                      </Tag>
                    )}
                    {isDraft && (
                      <Tag
                        color={draftStatus?.color}
                        icon={draftStatus?.icon}
                        className="tag-with-icon-color"
                      >
                        {draftStatus?.name}
                      </Tag>
                    )}
                  </Flex>

                  <Flex gap={8}>
                    {data.readOnly && (
                      <LockCloseIcon
                        style={{ color: "#BBBBBB", fontSize: 18 }}
                      />
                    )}
                    {!isDeleted && !isEnded && !isDraft && (
                      <Flex gap={4}>
                        {!data.readOnly && (
                          <>
                            <Button
                              title="Редактировать"
                              variant="text"
                              type="text"
                              icon={<EditIcon />}
                              onClick={handleEditTask}
                              style={{ padding: 0 }}
                              disabled={data.readOnly}
                              className="hide-desktop"
                            />
                            <Button
                              title={
                                subscribeStatus
                                  ? "Отписаться от обновлений"
                                  : "Подписаться на обновления"
                              }
                              variant="text"
                              type={subscribeStatus ? "primary" : "text"}
                              icon={<SpeakerphoneIcon />}
                              onClick={handleSubscribe}
                              style={{ padding: 0 }}
                              disabled={subscribeLoading}
                            />
                            <Button
                              color="default"
                              variant="text"
                              type="text"
                              icon={<LinkIcon />}
                              onClick={async () => {
                                const result = await copyLinkToClipboard(
                                  window.location.href,
                                );
                                message.open({
                                  type: result.success ? "success" : "error",
                                  content: result.message,
                                  duration: 5,
                                });
                              }}
                              style={{ padding: 0 }}
                            />
                          </>
                        )}
                        <div className="hide-desktop">
                          <Dropdown
                            menu={{
                              items: getTaskDropdownMenuItems({
                                isReadOnly: data?.readOnly,
                                isMobile: true,
                                isUserCanEditFiles,
                                handleCreateTemplate,
                                handleCopyTask,
                                handleDownloadTask,
                                handleAddNew,
                                onUploadFile,
                                handleAddFileLink,
                                setIsModalAttachedResourcesShow,
                                setCurrentAttachedResource,
                                handleDelete: () => handleDelete(data),
                              }),
                            }}
                            trigger={["click"]}
                            placement="bottomRight"
                          >
                            <Button
                              color="default"
                              variant="text"
                              type="text"
                              icon={<DotsHorizontalIcon />}
                            />
                          </Dropdown>
                        </div>
                      </Flex>
                    )}

                    <ConfigProvider
                      theme={{
                        token: {
                          colorPrimary: "#E66A25",
                        },
                      }}
                    >
                      <Button
                        type="primary"
                        icon={<InformationIcon />}
                        onClick={() => setIsModalInfoShow(true)}
                        className="hide-desktop"
                      />
                    </ConfigProvider>
                  </Flex>
                </Flex>
              </Flex>

              <Flex gap={20}>
                <Flex gap={30} vertical className={styles.body}>
                  <Flex gap={10} vertical>
                    <Card>
                      <Flex vertical gap={30} justify="space-between">
                        <Flex vertical gap={15}>
                          <div style={{ color: "#777777" }}>
                            {`Создано ${formatISODateToCustomStr(
                              data.created_at,
                            )}, обновлено ${formatISODateToCustomStr(
                              data.updated_at,
                            )}`}
                          </div>
                          <Typography.Title
                            level={4}
                            style={{ margin: 0, fontSize: 15, fontWeight: 500 }}
                          >
                            {data.name}
                          </Typography.Title>

                          {isDescriptionEditing ? (
                            <Flex vertical gap={8}>
                              <MarkdownEditor
                                rows={5}
                                value={descriptionDraft}
                                onChange={setDescriptionDraft}
                                autoFocus
                              />
                              <Flex gap={8} justify="flex-end">
                                <Button
                                  onClick={handleCancelDescriptionEdit}
                                  disabled={isDescriptionSaving}
                                >
                                  Отменить
                                </Button>
                                <Button
                                  type="primary"
                                  onClick={handleSaveDescription}
                                  loading={isDescriptionSaving}
                                >
                                  Сохранить
                                </Button>
                              </Flex>
                            </Flex>
                          ) : (
                            <Flex
                              gap={4}
                              align="flex-start"
                              className={styles.descriptionRow}
                            >
                              <CopyableText
                                text={data.description || ""}
                                className={styles.descriptionCopyable}
                              >
                                {data.description ? (
                                  <MarkdownText
                                    value={data.description}
                                    className={styles.descriptionText}
                                  />
                                ) : (
                                  <div
                                    className={`${styles.fakeDescriptionLock} ${styles.descriptionText}`}
                                  >
                                    Описание
                                  </div>
                                )}
                              </CopyableText>
                              {isUserCanEditDescription && (
                                <Tooltip title="Редактировать">
                                  <Button
                                    variant="text"
                                    type="text"
                                    icon={<EditIcon />}
                                    onClick={handleEditDescription}
                                    className={styles.descriptionAction}
                                    style={{ padding: 0 }}
                                  />
                                </Tooltip>
                              )}
                            </Flex>
                          )}
                        </Flex>
                      </Flex>
                    </Card>

                    {data.location && address && (
                      <Card>
                        <Flex vertical gap={10}>
                          <Typography.Title
                            level={4}
                            style={{
                              marginTop: 0,
                              fontSize: 15,
                              fontWeight: 500,
                            }}
                          >
                            Местоположение
                          </Typography.Title>

                          <Typography.Text>{address}</Typography.Text>

                          <Map
                            initialViewState={{
                              longitude: data.location.y,
                              latitude: data.location.x,
                              zoom: 12,
                            }}
                            style={{ width: 300, height: 300 }}
                            mapStyle={MAP_STYLE}
                            attributionControl={false}
                            cursor={"grabbing"}
                          >
                            {/* Стандартные контролы карты */}
                            <FullscreenControl />

                            <NavigationControl showCompass={false} />

                            <Marker
                              longitude={data.location.y}
                              latitude={data.location.x}
                              anchor="bottom"
                              offset={[0, 0]}
                            />
                          </Map>
                        </Flex>
                      </Card>
                    )}

                    {data.is_recurring && (
                      <div className="hide-desktop">
                        <PeriodicalSection
                          data={data}
                          isAdmins={isAdmins}
                          unit_uuid={unit_uuid}
                          repeatRules={repeatRules}
                        />
                      </div>
                    )}
                  </Flex>

                  <Tabs
                    activeKey={activeTab}
                    items={[
                      {
                        key: "subtasks",
                        label: (
                          <Flex align="center" gap={5}>
                            <span>Подзадачи</span>
                            <span style={{ fontWeight: 400, color: "#777777" }}>
                              {children?.length || 0}
                            </span>
                          </Flex>
                        ),
                        children: (
                          <SubtasksTab
                            data={children}
                            isAdmins={isAdmins}
                            pending={childrenPending}
                            statuses={statuses}
                            priorities={priorities}
                            taskTypes={taskTypes}
                            handleCopySubtask={handleCopySubtask}
                            handleEditSubtask={handleEditSubtask}
                            handleDelete={handleDelete}
                            handleAddNew={handleAddNew}
                            handlePublishSubtask={onPublishDraft}
                            handleAddActualCosts={(v) => {
                              setCurrentTask(v);
                              setIsModalActualLaborCostsShow(true);
                            }}
                            handleEditPlannedCosts={(v) => {
                              setCurrentTask(v);
                              setIsModalEditPlannedLaborCostsShow(true);
                            }}
                            unit_uuid={unit_uuid}
                            readOnly={data.readOnly || isDeleted || isEnded}
                            showDeletedTasks={showDeletedSubtasks}
                            onShowDeletedTasksChange={
                              handleShowDeletedSubtasksChange
                            }
                          />
                        ),
                      },
                      {
                        key: "files",
                        label: (
                          <Flex align="center" gap={5}>
                            <span>Вложения</span>
                            <span style={{ fontWeight: 400, color: "#777777" }}>
                              {files?.length || 0}
                            </span>
                          </Flex>
                        ),
                        children: (
                          <FilesTab
                            data={files}
                            pending={filesPending}
                            unit_uuid={data.unit_uuid}
                            handleAddNew={onUploadFile}
                            handleEditFile={handleEditFile}
                            handleDeleteFile={handleDeleteFile}
                            handleAddFileLink={handleAddFileLink}
                            readOnly={!isUserCanEditFiles}
                            uploadsProps={uploadsProps}
                          />
                        ),
                      },
                      {
                        key: "comments",
                        label: (
                          <Flex align="center" gap={5}>
                            <span>Комментарии</span>
                            <span style={{ fontWeight: 400, color: "#777777" }}>
                              {comments?.length || 0}
                            </span>
                          </Flex>
                        ),
                        children: (
                          <CommentsTab
                            data={comments}
                            pending={commentsPending}
                            taskUuid={id}
                            taskFileContext={{
                              folder_uuid: data.folder_uuid || data.task_number,
                              unit_uuid: data.unit_uuid,
                              folder: {
                                uuid: data.task_number,
                                label: data.task_number,
                              },
                            }}
                            units={units}
                            readOnly={data.readOnly || isDeleted || isEnded}
                            isAdmins={isAdmins}
                            currentUser={user}
                            onCreate={handleCreateComment}
                            onUpdate={handleUpdateComment}
                            onDelete={handleDeleteComment}
                            onNavigateToComment={handleNavigateToComment}
                            onCopyLink={handleCopyCommentLink}
                            onToggleReaction={handleToggleCommentReaction}
                            onDeleteAttachment={handleDeleteCommentAttachment}
                          />
                        ),
                      },
                      {
                        key: "resources",
                        label: (
                          <Flex align="center" gap={5}>
                            <span>Ресурсы</span>
                            <span style={{ fontWeight: 400, color: "#777777" }}>
                              {objectsCount}
                            </span>
                          </Flex>
                        ),
                        children: (
                          <ObjectsTab
                            data={objects}
                            pending={objectsPending}
                            isAdmins={isAdmins}
                            readOnly={!isUserCanEditObjectsList}
                            handleAddNested={(item) => {
                              setCurrentAttachedResource({
                                ...item.item,
                                definition_uuid: item.item.type.definition.uuid,
                                objects: item.item.objects,
                                objectUuid: item.uuid,
                              });

                              setIsModalNestedResourcesShow(true);
                            }}
                            handleAddNew={() => {
                              setCurrentAttachedResource(null);
                              setIsModalAttachedResourcesShow(true);
                            }}
                            handleEdit={(item) => {
                              setCurrentAttachedResource({
                                ...item,
                                definition_uuid: item.type.definition.uuid,
                                objects: item.objects,
                                objectUuid: item.uuid,
                              });

                              setIsModalAttachedResourcesShow(true);
                            }}
                            handleDelete={handleDeleteObject}
                          />
                        ),
                      },

                      {
                        key: "checklist",
                        label: (
                          <Flex align="center" gap={5}>
                            <span>Чек-лист</span>
                            <span style={{ fontWeight: 400, color: "#777777" }}>
                              {checklist?.length || 0}
                            </span>
                          </Flex>
                        ),
                        children: (
                          <ChecklistTab
                            data={checklist}
                            pending={checklistPending}
                            readOnly={!isUserCanEditChecklist}
                            canToggle={isUserCanToggleChecklist}
                            handleEditItem={handleEditChecklistItem}
                            handleCheckItem={handleCheckChecklistItem}
                            handleDeleteItem={handleDeleteChecklistItem}
                            handleDeleteList={handleDeleteList}
                          />
                        ),
                      },
                      ...(isDraft
                        ? []
                        : [
                            {
                              key: "laborCosts",
                              label: (
                                <>
                                  {data?.do_not_exceed_children &&
                                    (data.planned_costs == null ||
                                      Number(data.planned_costs) === 0) && (
                                      <Badge
                                        count={
                                          <ExclamationCircleIcon
                                            style={{
                                              color: "#FF4D4F",
                                              marginRight: 5,
                                              paddingBottom: 2,
                                              fontSize: 15,
                                            }}
                                          />
                                        }
                                      />
                                    )}
                                  <span>Трудозатраты</span>
                                </>
                              ),
                              children: (
                                <LaborCostsTab
                                  data={laborCosts}
                                  planned={Number(data.planned_costs)}
                                  actual={Number(data.actual_costs)}
                                  childrenPlanned={Number(
                                    data.sum_children_planned_costs,
                                  )}
                                  childrenActual={Number(
                                    data.sum_children_actual_costs,
                                  )}
                                  requireActualsOnAcceptance={
                                    data.require_actuals_on_acceptance
                                  }
                                  doNotExceedChildren={
                                    data.do_not_exceed_children
                                  }
                                  taskHasChildren={children?.length > 0}
                                  pending={laborCostsPending}
                                  pagination={laborCostsPagination}
                                  canEditPlanned={
                                    isUserCanEditPlannedLaborCosts
                                  }
                                  canAddActual={
                                    !isTaskStatusFinal(status) &&
                                    !status.is_for_draft
                                  }
                                  handleEditPlannedLaborCosts={() => {
                                    setCurrentTask(data);
                                    setIsModalEditPlannedLaborCostsShow(true);
                                  }}
                                  handleAddLaborCosts={() => {
                                    setCurrentTask(data);
                                    setIsModalActualLaborCostsShow(true);
                                  }}
                                  handleEditLaborCosts={handleEditLaborCost}
                                  handleDeleteLaborCosts={handleDeleteLaborCost}
                                  isAdmins={isAdmins}
                                  onTableChange={handleLaborCostsTableChange}
                                />
                              ),
                            },
                          ]),
                      // Всегда последняя вкладка
                      {
                        key: "history",
                        label: "История",
                        children: (
                          <HistoryTab
                            data={logs}
                            pending={logsPending}
                            pagination={logsPagination}
                            onTableChange={handleHistoryTableChange}
                          />
                        ),
                      },
                    ]}
                    onTabClick={(key) => setActiveTab(key)}
                  />
                </Flex>

                <div className={styles.aside}>
                  <Flex
                    vertical
                    gap={10}
                    style={{ position: "sticky", top: 0 }}
                  >
                    <Card>
                      <InfoSection
                        data={data}
                        status={status}
                        priority={priority}
                        taskType={taskType}
                        is_overdue={is_overdue}
                        isUserCanEditExecutor={isUserCanEditExecutor}
                        isUserCanEditStartDate={isUserCanEditStartDate}
                        isUserCanEditDueDate={isUserCanEditDueDate}
                        isUserCanEditCoexecutors={isUserCanEditCoexecutors}
                        isUserCanEditAcceptor={isUserCanEditAcceptor}
                        isUserCanEditSprint={isUserCanEditSprint}
                        handleEditTask={handleEditTask}
                        showSetMeExecutor={showSetMeExecutor}
                        showSetMeAcceptor={showSetMeAcceptor}
                        setMeExecutor={setMeExecutor}
                        setMeAcceptor={setMeAcceptor}
                        setIsGroupTask={setIsGroupTask}
                      />
                    </Card>

                    {data.is_recurring && (
                      <PeriodicalSection
                        data={data}
                        isAdmins={isAdmins}
                        unit_uuid={unit_uuid}
                        repeatRules={repeatRules}
                      />
                    )}

                    {!isDeleted && !isEnded && (
                      <Card>
                        <Flex justify="space-between" align="center">
                          <Flex gap={6}>
                            <Button
                              onClick={handleEditTask}
                              icon={<EditIcon style={{ fontSize: 14 }} />}
                              disabled={data.readOnly}
                            >
                              Редактировать
                            </Button>

                            <Dropdown
                              menu={{
                                items: getTaskDropdownMenuItems({
                                  isReadOnly: data?.readOnly,
                                  isUserCanEditFiles,
                                  handleCreateTemplate,
                                  handleCopyTask,
                                  handleDownloadTask,
                                  handleAddNew,
                                  onUploadFile,
                                  handleAddFileLink,
                                  setIsModalAttachedResourcesShow,
                                  setCurrentAttachedResource,
                                }),
                              }}
                              trigger={["click"]}
                              placement="bottomRight"
                            >
                              <Button>
                                Действия
                                <ChevronDownIcon style={{ fontSize: 14 }} />
                              </Button>
                            </Dropdown>
                          </Flex>

                          <Button
                            disabled={data.readOnly}
                            onClick={() => handleDelete(data)}
                            icon={<TrashIcon style={{ fontSize: 14 }} />}
                          ></Button>
                        </Flex>

                        {isDraft && !isParentDraft && (
                          <>
                            <Divider style={{ borderColor: "#E9E9E9" }} />

                            <Flex vertical gap={10} align="flex-start">
                              <Button
                                type="primary"
                                onClick={() => onPublishDraft(data)}
                              >
                                Опубликовать
                              </Button>

                              <Typography.Text type="secondary">
                                Публикация черновика переводит его в&nbsp;статус
                                новой задачи с&nbsp;тэгом
                                &laquo;не&nbsp;начата&raquo;. Перевести задачу
                                обратно в&nbsp;черновик не&nbsp;получится.
                              </Typography.Text>
                            </Flex>
                          </>
                        )}
                      </Card>
                    )}
                  </Flex>
                </div>
              </Flex>
            </Flex>

            <Modal
              title="Сохранить изменения в описании?"
              open={!!pendingDescriptionNavigation}
              onCancel={handleStayOnTask}
              footer={[
                <Button key="stay" onClick={handleStayOnTask}>
                  Отмена
                </Button>,
                <Button
                  key="discard"
                  onClick={handleDiscardDescriptionAndNavigate}
                  disabled={isDescriptionSaving}
                >
                  Не сохранять
                </Button>,
                <Button
                  key="save"
                  type="primary"
                  onClick={handleSaveDescriptionAndNavigate}
                  loading={isDescriptionSaving}
                  disabled={!isUserCanEditDescription}
                >
                  Сохранить
                </Button>,
              ]}
            >
            </Modal>

            {isModalShow && (
              <TaskModal
                isAdmins={isAdmins}
                data={modalItem}
                statuses={statuses}
                priorities={priorities}
                taskTypes={taskTypes}
                units={units}
                visible={isModalShow}
                onSave={handleSaveItem}
                onCancel={() => setIsModalShow(false)}
                unit_uuid={unit_uuid}
              />
            )}

            {isModalFileShow && (
              <FileModal
                data={currentFile}
                visible={isModalFileShow}
                units={units}
                disableUnit
                disableFolder
                onSave={onFileModalSave}
                onCancel={() => setIsModalFileShow(false)}
              />
            )}

            {isCopyModalShow && (
              <CopyTaskModal
                visible={isCopyModalShow}
                onCancel={() => setIsCopyModalShow(false)}
                onSave={handleSaveCopyItem}
                task={subtaskCopyData || data}
                units={units}
                isAdmins={isAdmins}
                unit_uuid={unit_uuid}
              />
            )}

            {isTemplateModalShow && (
              <TaskTemplatesModal
                isAdmins={isAdmins}
                task={data?.uuid}
                statuses={statuses}
                priorities={priorities}
                taskTypes={taskTypes}
                units={units}
                visible={isTemplateModalShow}
                onSave={handleSaveTemplate}
                onCancel={() => setIsTemplateModalShow(false)}
              />
            )}

            {isModalInfoShow && (
              <TaskInfoModal
                data={data}
                readOnly={data.readOnly || isDeleted || isEnded}
                status={status}
                priority={priority}
                taskType={taskType}
                is_overdue={is_overdue}
                isUserCanEditExecutor={isUserCanEditExecutor}
                isUserCanEditStartDate={isUserCanEditStartDate}
                isUserCanEditDueDate={isUserCanEditDueDate}
                isUserCanEditCoexecutors={isUserCanEditCoexecutors}
                isUserCanEditAcceptor={isUserCanEditAcceptor}
                isUserCanEditSprint={isUserCanEditSprint}
                showSetMeExecutor={showSetMeExecutor}
                showSetMeAcceptor={showSetMeAcceptor}
                setMeExecutor={setMeExecutor}
                setMeAcceptor={setMeAcceptor}
                setIsGroupTask={setIsGroupTask}
                visible={isModalInfoShow}
                onOk={() => {
                  setIsModalInfoShow(false);
                  handleEditTask();
                }}
                onCancel={() => setIsModalInfoShow(false)}
              />
            )}

            {isModalAttachedResourcesShow && (
              <AttachingObjectsToTaskModal
                attachedDefinitions={
                  objects?.map((item) => item.type.definition.uuid) || []
                }
                value={currentAttachedResource}
                task_uuid={id}
                visible={isModalAttachedResourcesShow}
                onSave={onAddNewObject}
                onCancel={() => setIsModalAttachedResourcesShow(false)}
                isAdmins={isAdmins}
              />
            )}

            {isModalNestedResourcesShow && currentAttachedResource && (
              <SingleObjectNestedModal
                attachedDefinitions={
                  objects?.map((item) => item.type.definition.uuid) || []
                }
                objectUuid={currentAttachedResource?.objectUuid}
                value={currentAttachedResource}
                task_uuid={id}
                visible={isModalNestedResourcesShow}
                onSave={onAddNewObject}
                onCancel={() => setIsModalNestedResourcesShow(false)}
                isAdmins={isAdmins}
              />
            )}

            {isModalEditPlannedLaborCostsShow && (
              <PlannedLaborCostsModal
                data={currentTask}
                visible={isModalEditPlannedLaborCostsShow}
                parentDoNotExceedChildren={
                  currentTask?.parent?.do_not_exceed_children || false
                }
                plannedUpLimit={
                  currentTask?.uuid
                    ? (currentTask?.parent?.available_limit_for_children ??
                        null) + currentTask?.planned_costs
                    : (currentTask?.parent?.available_limit_for_children ??
                      null)
                }
                plannedLowLimit={currentTask?.sum_children_planned_costs}
                onCancel={() => {
                  setIsModalEditPlannedLaborCostsShow(false);
                  setCurrentTask(null);
                }}
                onSave={handleSavePlannedCosts}
              />
            )}

            {isModalActualLaborCostsShow && (
              <ActualLaborCostsModal
                data={currentLaborCost}
                task_uuid={currentTask?.uuid || id}
                unit_uuid={data.unit_uuid}
                visible={isModalActualLaborCostsShow}
                onCancel={() => {
                  setIsModalActualLaborCostsShow(false);
                  setCurrentLaborCost(null);
                  setCurrentTask(null);
                }}
                onSave={onLaborCostSave}
                isAdmins={isAdmins}
              />
            )}

            {isSystemModalShow && (
              <FileLinkModal
                visible={isSystemModalShow}
                onCancel={() => setIsSystemModalShow(false)}
                unit_uuid={unit_uuid}
                onSave={handleSaveFileLink}
              />
            )}

            {isPublishModalShow && (
              <PublishModal
                data={modalItem}
                statuses={statuses}
                visible={isPublishModalShow}
                onSave={handlePublishSave}
                onCancel={() => setIsPublishModalShow(false)}
              />
            )}

            {TASK_EXPORT_ENABLED && isDownloadTaskModalShow && (
              <ExportTasksModal
                tasks={[data.uuid]}
                visible={isDownloadTaskModalShow}
                onSave={handleExportTask}
                onCancel={() => setIsDownloadTaskModalShow(false)}
                onClearAndCancel={() => setIsDownloadTaskModalShow(false)}
                isAdmins={isAdmins}
              />
            )}
          </>
        )
      )}
    </>
  );
};

export default Task;
